import * as React from "react";
import { useParams, Link } from "react-router-dom";
import { AlertTriangle, AlertCircle, Info, X, ChevronRight, ShieldAlert } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Full Alerts page — filter by severity + module, dismiss with audit.
 * Alerts are cross-module signals from the rules engine. Every alert
 * carries a link back to the source entity.
 */
const SEV = {
  alert: { icon: AlertCircle, dot: "bg-red-500", label: "Alert", chip: "chip-coral" },
  warn:  { icon: AlertTriangle, dot: "bg-amber-500", label: "Warn", chip: "chip-amber" },
  info:  { icon: Info, dot: "bg-brand-violet", label: "Info", chip: "chip-violet" },
};

function entityHref(companyId, ev) {
  const t = ev.target_module;
  const id = ev.entity_id;
  if (!id) return null;
  if (t === "bookings") return `/c/${companyId}/m/bookings?open=${id}`;
  if (t === "rotas")    return `/c/${companyId}/m/rotas`;
  if (t === "pay")      return `/c/${companyId}/m/pay?open=${id}`;
  if (t === "books")    return `/c/${companyId}/m/books?open=${id}`;
  return null;
}

export default function Alerts() {
  const { companyId } = useParams();
  const [rows, setRows] = React.useState(null);
  const [rules, setRules] = React.useState([]);
  const [counts, setCounts] = React.useState(null);
  const [severity, setSeverity] = React.useState("");
  const [module, setModule] = React.useState("");
  const [showDismissed, setShowDismissed] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${companyId}/alerts`, {
        params: { limit: 200, dismissed: showDismissed, severity: severity || undefined, module: module || undefined },
      });
      setRows(data.alerts);
      if (!showDismissed) setCounts(data.counts);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }, [companyId, severity, module, showDismissed]);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    api.get(`/companies/${companyId}/alerts/rules`).then(({data}) => setRules(data.rules));
  }, [companyId]);
  React.useEffect(() => {
    const on = (e) => { const t = e.detail?.type; if (t && t.startsWith("rule_event.")) load(); };
    window.addEventListener("fable:event", on);
    return () => window.removeEventListener("fable:event", on);
  }, [load]);

  async function dismiss(ev) {
    try {
      await api.post(`/companies/${companyId}/alerts/${ev.id}/dismiss`, {});
      toast.success("Dismissed");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }

  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1200px]" data-testid="alerts-page">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="h-8 w-8 rounded-md bg-accent text-brand-violet flex items-center justify-center">
          <ShieldAlert size={17}/>
        </div>
        <div>
          <div className="text-[19px] font-bold tracking-tight text-brand-ink">Operational alerts</div>
          <div className="text-[11.5px] text-brand-muted">Cross-module rules the ops team should act on.</div>
        </div>
        <div className="flex-1"/>
        {counts && (
          <div className="flex items-center gap-1.5">
            <span className="chip chip-coral mono-num text-[10.5px]">{counts.alert} alert</span>
            <span className="chip chip-amber mono-num text-[10.5px]">{counts.warn} warn</span>
            <span className="chip chip-violet mono-num text-[10.5px]">{counts.info} info</span>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="card-panel mb-3">
        <div className="px-3 py-2 flex flex-wrap items-center gap-2">
          <label className="text-[11px] text-brand-muted">Severity</label>
          <select className="field-input !h-8 !w-32" value={severity}
                  onChange={(e)=>setSeverity(e.target.value)}
                  data-testid="alerts-severity">
            <option value="">Any</option>
            <option value="alert">Alert</option>
            <option value="warn">Warn</option>
            <option value="info">Info</option>
          </select>
          <label className="text-[11px] text-brand-muted ml-2">Module</label>
          <select className="field-input !h-8 !w-32" value={module}
                  onChange={(e)=>setModule(e.target.value)}
                  data-testid="alerts-module">
            <option value="">Any</option>
            <option value="bookings">Bookings</option>
            <option value="rotas">Rotas</option>
            <option value="pay">Pay</option>
            <option value="books">Books</option>
          </select>
          <div className="flex-1"/>
          <label className="inline-flex items-center gap-2 cursor-pointer text-[12px]">
            <input type="checkbox" checked={showDismissed}
                   onChange={(e)=>setShowDismissed(e.target.checked)}
                   data-testid="alerts-show-dismissed"/>
            <span>Show dismissed</span>
          </label>
        </div>
      </div>

      {/* List */}
      <div className="card-panel overflow-hidden" data-testid="alerts-list">
        <div className="table-scroll">
          <table className="fable-table">
            <thead>
              <tr>
                <th style={{width:"6%"}}>Sev</th>
                <th style={{width:"16%"}}>Rule</th>
                <th>Title</th>
                <th style={{width:"12%"}}>Module</th>
                <th style={{width:"18%"}}>When</th>
                <th style={{width:"14%"}} className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!rows && <tr><td colSpan={6} className="text-[12px] text-brand-muted py-6">Loading…</td></tr>}
              {rows && rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-[12px] text-brand-muted">
                  {showDismissed ? "No dismissed alerts" : "All clear — no open alerts."}
                </td></tr>
              )}
              {rows && rows.map((ev) => {
                const s = SEV[ev.severity] || SEV.info;
                const href = entityHref(companyId, ev);
                const dismissed = !!ev.dismissed_at;
                return (
                  <tr key={ev.id} data-testid={`alerts-row-${ev.id}`}>
                    <td>
                      <span className={`inline-flex items-center gap-1.5 chip ${s.chip} mono-num text-[10px]`}>
                        <span className={`h-2 w-2 rounded-full ${s.dot}`}/> {s.label}
                      </span>
                    </td>
                    <td className="mono-num text-[11px] text-brand-muted">{ev.rule_id}</td>
                    <td>
                      <div className="text-[12.5px] font-medium text-brand-ink">{ev.title}</div>
                      {ev.payload && Object.keys(ev.payload).length > 0 && (
                        <div className="mono-num text-[10.5px] text-brand-muted truncate max-w-[520px]">
                          {Object.entries(ev.payload).slice(0,3).map(([k,v]) => `${k}:${typeof v === 'object' ? '…' : String(v).slice(0,20)}`).join('  ')}
                        </div>
                      )}
                    </td>
                    <td className="text-[12px] text-brand-muted">{ev.target_module}</td>
                    <td className="mono-num text-[11px] text-brand-muted">
                      {ev.created_at.replace('T', ' ').slice(0,16)}
                    </td>
                    <td className="text-right">
                      <div className="flex justify-end items-center gap-1">
                        {href && !dismissed && (
                          <Link to={href} className="btn-ghost !h-7 !px-2" data-testid={`alerts-open-${ev.id}`}>
                            Open <ChevronRight size={12}/>
                          </Link>
                        )}
                        {!dismissed && (
                          <button className="btn-ghost !h-7 !px-2 text-brand-coral"
                                  onClick={() => dismiss(ev)}
                                  data-testid={`alerts-dismiss-${ev.id}`}>
                            <X size={13}/>
                          </button>
                        )}
                        {dismissed && (
                          <span className="mono-num text-[10.5px] text-brand-faint">dismissed</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Rule registry legend */}
      <div className="mt-4">
        <div className="micro-label mb-1">Rule registry</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {rules.map((r) => {
            const s = SEV[r.severity] || SEV.info;
            return (
              <div key={r.id} className="card-panel px-3 py-2 flex items-start gap-2">
                <span className={`mt-0.5 h-2 w-2 rounded-full ${s.dot}`}/>
                <div className="min-w-0">
                  <div className="text-[12.5px] font-semibold text-brand-ink">{r.name}</div>
                  <div className="text-[11px] text-brand-muted">{r.description}</div>
                  <div className="mono-num text-[10px] text-brand-faint mt-0.5">
                    {r.id} · sources: {r.sources.join(", ")} · target: {r.target_module}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
